import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const FssTheme = definePreset(Aura, {
  // One corner radius everywhere (8px) — Aura's built-in components read these primitives.
  primitive: {
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '8px',
      md: '8px',
      lg: '8px',
      xl: '8px',
    },
  },
  semantic: {
    primary: {
      25: '#FEFDF0',
      50: '#FEFBE8',
      100: '#FEF7C3',
      200: '#FEEE95',
      300: '#FDE272',
      400: '#FAC515',
      500: '#EAAA08',
      600: '#CA8504',
      700: '#A15C07',
      800: '#854A0E',
      900: '#713B12',
      950: '#542C0D',
    },
    secondary: {
      25: '#F5F8FF',
      50: '#EFF4FF',
      100: '#D1E0FF',
      200: '#B2CCFF',
      300: '#84ADFF',
      400: '#6794EE',
      500: '#4278E4',
      600: '#2A66DE',
      700: '#275ECD',
      800: '#1646A4',
      900: '#0C3891',
      950: '#072460',
    },
    gray: {
      25: '#F8FAFD',
      50: '#F7F8FA',
      100: '#F0F2F5',
      200: '#EBEDF0',
      300: '#E1E2E5',
      400: '#ADB0B8',
      500: '#7E838F',
      600: '#535862',
      700: '#414651',
      800: '#252B37',
      900: '#181D27',
      950: '#0A0D12',
    },
    red: {
      25: '#FFFBFA',
      50: '#FEF3F2',
      100: '#FEE4E2',
      200: '#FECDCA',
      300: '#FDA29B',
      400: '#F97066',
      500: '#F04438',
      600: '#D92D20',
      700: '#B42318',
      800: '#912018',
      900: '#7A271A',
      950: '#55160C',
    },
    warning: {
      25: '#FFFCF5',
      50: '#FFFAEB',
      100: '#FEF0C7',
      200: '#FEDF89',
      300: '#FEC84B',
      400: '#FDB022',
      500: '#F79009',
      600: '#DC6803',
      700: '#B54708',
      800: '#93370D',
      900: '#7A2E0E',
      950: '#4E1D09',
    },
    success: {
      25: '#F6FEF9',
      50: '#ECFDF3',
      100: '#DCFAE6',
      200: '#ABEFC6',
      300: '#75E0A7',
      400: '#47CD89',
      500: '#17B26A',
      600: '#079455',
      700: '#067647',
      800: '#085D3A',
      900: '#074D31',
      950: '#053321',
    },
    mask: {
      background: 'rgba(10, 13, 18, 0.7)',
    },
    colorScheme: {
      dark: {
        primary: {
          color: '#1C1A19',
        },
        mask: {
          background: 'rgba(10, 13, 18, 0.7)',
        },
      },
      light: {
        // Map Aura's slate surface palette to the Figma Gray scale so derived
        // tokens (content/formField border, table cell border, hovers) match the design.
        surface: {
          0: '#FFFFFF',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}',
        },
        primary: {
          color: 'red',
        },
        text: {
          color: '#252B37',
        },
        mask: {
          background: 'rgba(10, 13, 18, 0.7)',
        },
        navigation: {
          item: {
            icon: {
              color: '#7E838F',
              focusColor: '#414651',
            },
          },
        },
      },
    },
  },
  components: {
    // custom button tokens and additional style
    button: {
      colorScheme: {
        light: {
          root: {
            borderRadius: '8px',
            paddingX: '0.875rem',
            paddingY: '0.625rem',
            sm: {
              paddingX: '0.75rem',
              paddingY: '0.5rem',
            },
            // Large CTA (package cards) — 56px tall, as on the public price list.
            lg: {
              paddingX: '1.5rem',
              paddingY: '1.25rem',
              fontSize: '1rem',
            },
          },
        },
      },
      css: ({ dt }: any) => `
        .p-buttongroup .selected-tab .p-button .p-button-label {
          color: ${dt('gray-700')};
        }

        .p-button {
          gap: 0.25rem;
          border: 0;
          white-space: nowrap; /* labels like "Zakaži termin" never break into two lines */
          display: flex;
          justify-content: space-between;
          align-items: center;

           .p-buttongroup & {
            background: transparent;
            color: ${dt('gray-500')};
            border: 1px solid ${dt('gray-300')};

            &:hover {
              background-color: ${dt('gray-100')};
              border: 1px solid ${dt('gray-300')};
              color: ${dt('gray-500')};
            }
          }

          &.primary {
            background: ${dt('gray-900')};
            color: white;

            &:hover {
              background: ${dt('gray-800')};
              border: 0;
            }

            &:focus-visible {
              outline-color: #717680;
              outline-style: solid;
              outline-width: 1px;
            }

            &:disabled {
              background: ${dt('gray-100')};
              border: 1px solid ${dt('gray-200')};
              color: ${dt('gray-400')};
              /* Disabled colors come from Figma; PrimeNG's default disabled opacity would wash them out */
              opacity: 1;

              /* Labels are often wrapped in .text-white (!important); keep disabled text readable */
              .text-white {
                color: ${dt('gray-400')} !important;
              }
            }
          }

          &.success {
            background: ${dt('success-500')};
            color: white;

            &:hover {
              background: ${dt('success-400')};
              border: 0;
            }

            &:focus-visible {
              outline-color: #717680;
              outline-style: solid;
              outline-width: 1px;
            }

            &:disabled {
              background: ${dt('gray-100')};
              border: 1px solid ${dt('gray-200')};
              color: ${dt('gray-400')};

              .text-white {
                color: ${dt('gray-400')} !important;
              }
            }
          }

          &.secondary {
            background: #FFFFFF;
            border: 1px solid ${dt('gray-300')};
            color: ${dt('gray-900')};

            &:hover {
              background: ${dt('gray-50')};
              border: 1px solid ${dt('gray-300')};
              color: ${dt('gray-900')};
            }

            &:focus-visible {
              outline-color: #717680;
              outline-style: solid;
              outline-width: 1px;
            }

            &:disabled {
              background: #FFFFFF;
              border: 1px solid ${dt('gray-200')};
              color: ${dt('gray-400')};
            }
          }

          &.outline {
            background: #FFFFFF;
            border: 1px solid ${dt('gray-900')};
            color: ${dt('gray-900')};

            &:hover {
              background: ${dt('gray-50')};
              border: 1px solid ${dt('gray-900')};
              color: ${dt('gray-900')};
            }

            &:focus-visible {
              outline-color: #717680;
              outline-style: solid;
              outline-width: 1px;
            }

            &:disabled {
              background: #FFFFFF;
              border: 1px solid ${dt('gray-200')};
              color: ${dt('gray-400')};
            }
          }

          &.tertiary {
            background: transparent;
            color: ${dt('gray-700')};

            &:hover {
              background: transparent;
              text-decoration: underline;
              border: none;
              color: ${dt('gray-700')};
            }

            &:focus-visible {
              outline-color: #717680;
              outline-style: solid;
              outline-width: 1px;
            }

            &:disabled {
              background: transparent;
              color: ${dt('gray-400')};
              text-decoration: none;
            }
          }

          &.link {
            background: transparent;
            border: none;
            padding: 0;
            gap: 0.25rem;
            color: #2970FF;
            font-size: 14px;
            line-height: 20px;
            font-weight: 600;

            &:not(:disabled):hover {
              background: transparent;
              border: none;
              color: #155EEF;
            }

            &:focus-visible {
              outline-color: #717680;
              outline-style: solid;
              outline-width: 1px;
            }

            &:disabled {
              background: transparent;
              color: ${dt('gray-400')};
            }
          }

          &.destructive {
            &.primary {
              background: ${dt('red-600')};
              color: white;

              &:hover {
                background: ${dt('red-700')};
                border: 0;
              }

              &:focus-visible {
                outline-color: #F04438;
                outline-style: solid;
                outline-width: 1px;
              }

              &:disabled {
                background: ${dt('gray-100')};
                border: 1px solid ${dt('gray-200')};
                color: ${dt('gray-400')};
              }
            }

            &.secondary {
              background: #FFFFFF;
              border: transparent;
              color: ${dt('red-600')};

              &:hover {
                background: ${dt('red-50')};
                border: transparent;
                color: ${dt('red-700')};
              }

              &:focus-visible {
                outline-color: #F04438;
                outline-style: solid;
                outline-width: 1px;
              }

              &:disabled {
                background: #FFFFFF;
                border: 1px solid ${dt('gray-200')};
                color: ${dt('gray-400')};
              }
            }

            &.outline {
              background: #FFFFFF;
              border: 1px solid ${dt('red-300')};
              color: ${dt('red-600')};

              &:hover {
                background: ${dt('red-50')};
                border: 1px solid ${dt('red-300')};
                color: ${dt('red-700')};
              }

              &:focus-visible {
                outline-color: #F04438;
                outline-style: solid;
                outline-width: 1px;
              }

              &:disabled {
                background: #FFFFFF;
                border: 1px solid ${dt('gray-200')};
                color: ${dt('gray-400')};
              }
            }

            &.tertiary {
              background: transparent;
              color: ${dt('red-600')};

              &:hover {
                background: transparent;
                text-decoration: underline;
                border: none;
                color: ${dt('red-700')};
              }

              &:focus-visible {
                outline-color: #F04438;
                outline-style: solid;
                outline-width: 1px;
              }

              &:disabled {
                background: transparent;
                color: ${dt('gray-400')};
                text-decoration: none;
              }
            }
          }
        }
      `,
    },
    dialog: {
      // Same horizontal inset for header, content and footer (--fss-dialog-padding in styles.scss).
      // Dialogs must not add their own side padding, otherwise content and actions misalign.
      header: {
        padding: 'var(--fss-dialog-padding) var(--fss-dialog-padding) 1rem var(--fss-dialog-padding)',
      },
      content: {
        padding: '0 var(--fss-dialog-padding) var(--fss-dialog-padding) var(--fss-dialog-padding)',
      },
      footer: {
        padding: '0 var(--fss-dialog-padding) var(--fss-dialog-padding) var(--fss-dialog-padding)',
      },
      colorScheme: {
        light: {
          root: {
            borderRadius: '8px',
            borderColor: 'transparent',
            shadow:
              '0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
          },
        },
      },
    },
    menu: {
      colorScheme: {
        light: {
          root: {
            borderRadius: '8px',
            borderColor: '{gray.300}',
            shadow:
              '0px 32px 64px -12px rgba(10, 13, 18, 0.14), 0px 5px 5px -2.5px rgba(10, 13, 18, 0.04)',
          },
        },
      },
      css: () => `
        .p-menu, .p-menu-item-link {
          font-family: inherit;
        }

        .p-menu-item-label {
          font-size: 14px;
          font-weight: 600;
        }
      `,
    },
    chip: {
      colorScheme: {
        light: {
          root: {
            borderRadius: '8px',
          },
        },
      },
      css: ({ dt }: any) => `
      .p-chip{
        display: inline-flex;
        justify-content: center;

        &.base{
          background: transparent !important;
          border: 1px solid ${dt('gray-300')};
          color: ${dt('gray-700')};

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        &.white{
          background: white !important;
          border: 1px solid ${dt('gray-300')};
          color: ${dt('gray-700')};

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        &.gray{
          background: ${dt('gray-500')} !important;
          border: none;
          color: white;

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        &.red{
          background: ${dt('red-500')} !important;
          border: none;
          color: white;

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        &.warn{
          background: ${dt('warning-500')} !important;
          border: none;
          color: white !important;

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        &.success{
          background: ${dt('success-500')} !important;
          border: none;
          color: white !important;

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        &.info{
          background: #2E90FA !important; /* Figma Blue/500 */
          border: none;
          color: white !important;

          &.sm {
            padding: 0.125rem 0.375rem;
            height: 1.375rem;
          }

          &.md {
            padding: 0.125rem 0.5rem;
            height: 1.5rem;
          }

          &.lg {
            padding: 0.25rem 0.625rem;
            min-width: 3.5625rem;
            height: 1.75rem;
          }
        }

        .p-chip-label{
          font-size: 12px;
          line-height: 18px;
          font-weight: 500;
          white-space: nowrap;
        }

        .p-chip-remove-icon{
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
      `,
    },
    select: {
      colorScheme: {
        light: {
          root: {
            paddingX: '0.75rem',
            paddingY: '0.5rem',
            background: 'transparent',
            disabledColor: '{gray.500}',
            disabledBackground: '{gray.50}',
            borderRadius: '8px',
          },
          option: {
            color: '{gray.900}',
            padding: '8px',
            selectedColor: '{gray.900}',
            selectedBackground: '{gray.50}',
            selectedFocusColor: '{gray.900}',
            selectedFocusBackground: '{gray.50}',
          },
          overlay: {
            borderRadius: '8px',
            borderColor: '{gray.200}',
          },
        },
      },
      css: ({ dt }: any) => `
      .p-select{
        .p-placeholder{
          font-size: 16px;
          line-height: 24px;
          font-weight: 400;
          color: ${dt('gray-400')};
        }
        &.p-focus{
          border: 2px solid ${dt('gray-500')} !important;
        }
      }
      `,
    },
    inputtext: {
      colorScheme: {
        light: {
          root: {
            color: '{gray.900}',
            borderColor: '{gray.300}',
            invalidBorderColor: '{red.300}',
            borderRadius: '8px',
            // Single value: padding-block takes it as top AND bottom (a second value made the
            // bottom 12px vs 8px top, so the text sat above the vertically centered icon).
            paddingY: '0.625rem',
            placeholderColor: '{gray.400}',
            focusBorderColor: '#717680',
            disabledBackground: '{gray.50}',
            disabledColor: '{gray.400}',
            focusRing: {
              width: '0',
              style: 'none',
              color: 'transparent',
              offset: '0',
              shadow: '0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px #181D271F',
            },
          },
        },
      },
    },
    textarea: {
      colorScheme: {
        light: {
          root: {
            color: '{gray.900}',
            borderColor: '{gray.300}',
            borderRadius: '8px',
            paddingY: '0.75rem',
            paddingX: '0.875rem',
            placeholderColor: '{gray.400}',
            focusBorderColor: '#717680',
            focusRing: {
              width: '0',
              style: 'none',
              color: 'transparent',
              offset: '0',
              shadow: '0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px #181D271F',
            },
            disabledBackground: '{gray.50}',
            disabledColor: '{gray.400}',
          },
        },
      },
    },
    tabs: {
      css: ({ dt }: any) => `
    /* Default styles */
    .p-tablist-tab-list {
      background: ${dt('gray.100')};
      border: 1px solid ${dt('gray.200')};
      border-radius: var(--fss-radius);
      padding: 0.25rem;
      gap: 0.25rem;
    }

    .p-tablist-content {
      background: ${dt('surface.card')};
    }

    .p-tab {
      background: transparent;
      color: ${dt('gray.500')};
      padding: 0.375rem 0.75rem;
      transition: none;
      border: none;

      &:not(.p-tab-active):not(.p-disabled):hover {
        color: ${dt('gray.900')};
      }

      &.p-tab-active {
        color: ${dt('gray.900')};
        border: none;
        background: white;
        border-radius: var(--fss-radius);
      }
    }

    .p-tablist-active-bar {
      background: transparent;
    }

    /* Transparent variant */
    .transparent .p-tablist,
    .transparent .p-tablist-content,
    .transparent .p-tablist-tab-list{
      background: transparent;
      border: none;
      gap: 0.25rem;
    }

    .transparent .p-tab {
      background: transparent;
      border: none;

      &:not(.p-tab-active):not(.p-disabled):hover {
        color: ${dt('gray.900')};
      }

      &.p-tab-active {
        background: transparent;
        color: ${dt('gray.900')};
      }
    }

    .transparent .p-tablist-active-bar {
      background: ${dt('gray.900')};
      height: 2px;
      inset-block-end: 0;
    }

    .transparent .p-tabpanels,
    .transparent .p-tabpanel {
      background: transparent;
    }
`,
    },
    toggleswitch: {
      colorScheme: {
        light: {
          root: {
            width: '36px',
            height: '20px',
            background: '{gray.200}',
            checkedBackground: '{gray.900}',
            checkedHoverBackground: '{gray.950}',
            hoverBackground: '{gray.200}',
            focusRing: {
              color: '#717680',
            },
            disabledBackground: '{gray.200}',
            borderRadius: '0.75rem',
          },
          handle: {
            disabledBackground: '{gray.50}',
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          root: {
            // 8px on a 16–20px box reads as a radio button; checkboxes keep a small radius.
            borderRadius: '{border.radius.xs}',
            sm: {
              width: '1rem',
              height: '1rem',
            },
            width: '1.25rem',
            height: '1.25rem',
            disabledBackground: '{gray.50}',
            borderColor: '{gray.300}',
            hoverBorderColor: '{gray.300}',
            checkedBackground: '{gray.900}',
            checkedHoverBackground: '{gray.900}',
            checkedHoverBorderColor: '{gray.900}',
            checkedBorderColor: 'transparent',
            checkedFocusBorderColor: '{gray.900}',
            focusRing: {
              color: '#717680',
            },
          },
          icon: {
            disabledColor: '{gray.300}',
          },
        },
      },
    },
    radiobutton: {
      colorScheme: {
        light: {
          root: {
            sm: {
              width: '1rem',
              height: '1rem',
            },
            width: '1.25rem',
            height: '1.25rem',
            disabledBackground: '{gray.50}',
            borderColor: '{gray.300}',
            hoverBorderColor: '{gray.300}',
            checkedBackground: '{gray.900}',
            checkedHoverBackground: '{gray.900}',
            checkedHoverBorderColor: '{gray.900}',
            checkedBorderColor: 'transparent',
            checkedFocusBorderColor: '{gray.900}',
            focusRing: {
              color: '#717680',
            },
          },
          icon: {
            disabledColor: '{gray.300}',
            sm: {
              size: '0.375rem',
            },
            size: '0.5rem',
          },
        },
      },
    },
    tooltip: {
      colorScheme: {
        light: {
          root: {
            background: '{gray.900}',
            color: 'white',
            padding: '0.5rem 0.75rem',
            borderRadius: '8px',
          },
        },
      },
    },
    progressbar: {
      colorScheme: {
        light: {
          root: {
            background: '{gray.200}',
            borderRadius: '0.25rem',
            height: '0.5rem',
          },
          value: {
            background: '{gray.700}',
          }
        }
      },
    },
    toast: {
      colorScheme: {
        light: {
          root: {
            width: '26.25rem',
            borderRadius: '8px',
          },
          content: {
            gap: '0.75rem',
          },
          text: {
            gap: '0.2rem',
          },
          success: {
            color: '#{success.900}',
            background: '{success.50}',
            borderColor: '{success.300}',
            detailColor: '{success.500}',
          },
          info: {
            color: '#{blue.900}',
            background: '{blue.50}',
            borderColor: '{blue.300}',
            detailColor: '{blue.500}',
          },
          warn: {
            color: '#{warning.900}',
            background: '{warning.50}',
            borderColor: '{warning.300}',
            detailColor: '{warning.500}',
          },
          error: {
            color: '#{red.900}',
            background: '{red.50}',
            borderColor: '{red.300}',
            detailColor: '{red.500}',
          },
          icon: {
          }
        },
      },
      css: ({ dt }: any) => `
        .p-toast-message-success .p-toast-message-icon {
          color: ${dt('success-500')};
          width: 1rem;
          height: 1rem;
        }
        .p-toast-message-info .p-toast-message-icon {
          color: ${dt('blue-500')};
          width: 1rem;
          height: 1rem;
        }
        .p-toast-message-warn .p-toast-message-icon {
          color: ${dt('warning-500')};
          width: 1rem;
          height: 1rem;
        }
        .p-toast-message-error .p-toast-message-icon {
          color: ${dt('red-500')};
          width: 1rem;
          height: 1rem;
        }
       .p-toast-message-success .p-toast-summary {
           color: ${dt('success-900')};
        }
        .p-toast-message-info .p-toast-summary {
           color: ${dt('blue-900')};
        }
        .p-toast-message-warn .p-toast-summary {
           color: ${dt('warning-900')};
        }
        .p-toast-message-error .p-toast-summary {
           color: ${dt('red-900')};
        }
        .p-toast-message-content {
          display: flex;
          width: 100%;
        }
      `,
    },
    datatable: {
      colorScheme: {
        light: {
          headerCell: {
            background: 'white',
            color: '{gray.400}',
            borderColor: '{gray.300}'
          },
        },
      },
      css: ({ dt }: any) => `
        .p-datatable-mask {
           display: none !important;
        }

        .p-datatable {
          border: 1px solid ${dt('gray-300')};
          border-radius: var(--fss-radius);
          overflow: hidden;
        }

        /* Mobile: every cell stays on one line and the table scrolls horizontally inside its card. */
        @media (max-width: 767.98px) {
          .p-datatable-table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .p-datatable-table {
            table-layout: auto !important;
            width: max-content;
            min-width: 100%;
          }

          .p-datatable-thead > tr > th,
          .p-datatable-tbody > tr > td {
            white-space: nowrap;
          }
        }

        .p-datatable-thead > th {
          border-bottom: 1px solid ${dt('gray-300')};
        }

        .p-paginator-next,
        .p-paginator-prev {
          display: none !important;
        }

        .p-paginator-page {
          color: ${dt('gray-600')} !important;
          font-size: 14px !important;
          line-height: 1.25rem !important;
          font-weight: 500 !important;



          &.p-paginator-page-selected,
          &:hover {
            color: ${dt('gray-800')} !important;
            font-size: 14px !important;
            line-height: 1.25rem !important;
            font-weight: 500 !important;
            background-color: ${dt('gray-50')} !important;
            border-radius: var(--fss-radius) !important;
          }
        }
      `,
    },
    datepicker: {
      colorScheme: {
        light: {
          panel: {
            borderRadius: '8px'
          },
          header: {
            borderColor: 'transparent',
          },
          weekDay: {
            color: '{gray.700}'
          }
        },
      },
      css: ({ dt }: any) => `
      .p-datepicker-prev-button,
      .p-datepicker-next-button {
        color: ${dt('gray-400')};

        &:hover {
          color: ${dt('gray-400')} !important;
          border: none !important;
          background: transparent !important;
        }
      }

      .p-datepicker-select-month,
      .p-datepicker-select-year{
        font-size: 1rem !important;
        line-height: 1.25rem !important;
        font-weight: 600 !important;
        color: ${dt('gray-700')} !important;
      }

       /* Today: no fill (Figma), but a dot under the number so it stands out even when disabled. */
       .p-datepicker-today .p-datepicker-day {
        background-color: transparent;
        position: relative;
        font-weight: 600;

        &::after {
          content: '';
          position: absolute;
          bottom: 0.3rem;
          left: 50%;
          width: 0.25rem;
          height: 0.25rem;
          margin-left: -0.125rem;
          border-radius: 50%;
          background-color: ${dt('gray-900')};
        }

        &.p-datepicker-day-selected::after {
          background-color: white;
        }
      }

      .p-datepicker-day {
        color: ${dt('gray-700')};
        background-color: transparent;
        border-radius: 50%;
        width: 2.5rem;
        height: 2.5rem;

        &.p-datepicker-day-selected {
          background-color: ${dt('gray-900')};
          border: 2px solid ${dt('gray-900')};
          color: white;
        }

        &.p-disabled {
          color: ${dt('gray-500')};
          background-color: transparent;
        }
      }

      .p-datepicker-other-month .p-datepicker-day {
        color: ${dt('gray-500')};
        background-color: transparent;
      }

      /* Mobile: 7 × 48px day cells overflow a 360px screen; let the inline calendar fit its container. */
      @media (max-width: 575.98px) {
        .p-datepicker-inline,
        .p-datepicker-inline .p-datepicker-panel,
        .p-datepicker-panel-inline {
          width: 100%;
          min-width: 0;
        }

        /* PrimeNG 21 wraps the inline panel in <p-motion> */
        .p-motion:has(> .p-datepicker-panel-inline) {
          width: 100%;
        }

        .p-datepicker-panel-inline .p-datepicker-calendar {
          flex: 1 1 auto;
        }

        .p-datepicker-day-cell {
          padding: 0.125rem;
        }

        .p-datepicker-day {
          width: 2.25rem;
          height: 2.25rem;
        }
      }
      `,
    },
  },
});
